/* Paramètres › Organisation · one card of a one-value list (wireframes-fully-ruling § 6; lib-pack-23 SEAMS § 1): « Catégories »
   (one per campaign) and « Emplacements » (one per support), drawn as WF-042 draws them beside « Étiquettes », and doing what
   the étiquettes' card does: « Nouvelle … », each row « Nom · N campagne(s) | support(s) » (the reader's scope, archived and
   deactivated items included, P3), « Renommer », « Fusionner » (choose the survivor: every item is re-pointed), « Supprimer »
   (a dialog names how many items go to none), the cap line. The list is written whole (setCategories / setPlaces: an id
   dropped = « Supprimer »); a refusal is the lib's line, said in the dialog. Who writes: the lib's rule (canWrite…List);
   a reader sees the rows and the lib's role line, no verb. Never shown to a customer, never a rule of the parcours. */
import { useState } from 'react'
import { Button, Card, Field, Input, Modal, Select } from '@/ui'
import { editRefusalLines, settled, writeMark } from '@/lib/writes'

/** An item of either list (lib/organisation › Category | Place). */
export interface OrgItem { id: string; name: string; position: number; createdAt: string }
/** A count as the lib gives it: a number, or the row's `counts` object ({ campaigns } | { supports }). */
export type OrgCount = number | { campaigns?: number; supports?: number } | undefined
const countOf = (c: OrgCount): number => (typeof c === 'number' ? c : (c?.campaigns ?? c?.supports ?? 0))

export interface OrgListProps<T extends OrgItem> {
  /** test id stem: `categories` | `emplacements` */
  kind: 'categories' | 'emplacements'
  /** one row's test id stem: `categorie` | `emplacement` */
  one: 'categorie' | 'emplacement'
  title: string
  help: string
  newLabel: string
  nameLabel: string
  placeholder: string
  intoLabel: string
  /** « campagne » / « campagnes » · « support » / « supports » */
  unit: [string, string]
  /** « cette catégorie » / « cet emplacement » */
  thisOne: string
  /** the delete dialog's line, given « N campagne(s) » / « N support(s) » */
  deleteEffect: (what: string) => string
  /** « N sur 30 catégories. » */
  capCount: (n: number) => string
  items: T[]
  counts: Record<string, OrgCount>
  max: number
  capLine: string
  roleLine: string
  writer: boolean
  issues: (name: string, list: T[], selfId?: string) => string | null
  add: (name: string) => T | string
  setList: (list: T[]) => void
  merge: (from: string, into: string) => void
  clean: (name: string) => string
  getList: () => T[]
}

type Dialog<T> = { kind: 'new' } | { kind: 'rename'; item: T } | { kind: 'merge'; item: T } | { kind: 'delete'; item: T } | null

export function OrganisationListe<T extends OrgItem>(p: OrgListProps<T>) {
  const [dialog, setDialog] = useState<Dialog<T>>(null)
  const [name, setName] = useState('')
  const [into, setInto] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const { items } = p
  const full = items.length >= p.max
  const plural = (n: number) => `${n} ${n > 1 ? p.unit[1] : p.unit[0]}`
  const n = (id: string) => countOf(p.counts[id])

  const close = () => { setDialog(null); setName(''); setInto(''); setError(null); setBusy(false) }
  /** the étiquettes' pattern: at once in the demo; with an API, once every write asked since `mark` has answered */
  const after = (mark: number) => { setBusy(true); void settled(mark).then(no => { if (no) { setBusy(false); setError(editRefusalLines(no)[0]) } else close() }) }
  const open = (d: NonNullable<Dialog<T>>) => {
    setError(null)
    setName(d.kind === 'rename' ? d.item.name : '')
    setInto(d.kind === 'merge' ? (items.find(t => t.id !== d.item.id)?.id ?? '') : '')
    setDialog(d)
  }
  const create = () => { if (busy) return; const mark = writeMark(); const r = p.add(name); if (typeof r === 'string') { setError(r); return } after(mark) }
  const rename = (t: T) => {
    if (busy) return
    const issue = p.issues(name, p.getList(), t.id)
    if (issue) { setError(issue); return }
    const mark = writeMark()
    p.setList(p.getList().map(x => (x.id === t.id ? { ...x, name: p.clean(name) } : x)))
    after(mark)
  }
  const merge = (t: T) => { if (busy || !into || into === t.id) return; const mark = writeMark(); p.merge(t.id, into); after(mark) }
  const remove = (t: T) => { if (busy) return; const mark = writeMark(); p.setList(p.getList().filter(x => x.id !== t.id)); after(mark) }
  const refused = error && <p className="f-help err" role="alert" style={{ margin: '8px 0 0' }} data-testid={`${p.kind}-refus`}>{error}</p>

  const d = dialog
  const target = d && d.kind === 'merge' ? items.find(t => t.id === into) : undefined
  return (
    <Card data-testid={`${p.kind}-carte`}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <h2 className="t-section" style={{ margin: 0, flex: 1 }}>{p.title}</h2>
        {p.writer && <Button size="sm" variant="primary" disabled={full} onClick={() => open({ kind: 'new' })}>{p.newLabel}</Button>}
      </div>
      <p className="f-help" style={{ margin: '8px 0 12px' }}>{p.help}</p>
      {!p.writer && <p className="f-help" style={{ margin: '0 0 12px' }} data-testid={`${p.kind}-lecture-seule`}>{p.roleLine}</p>}
      {items.length === 0
        ? <p className="f-help" style={{ margin: 0 }} data-testid={`${p.kind}-vide`}>Aucun terme créé.</p>
        : <div className="list" data-testid={`${p.kind}-liste`}>
          {items.map(t => (
            <div key={t.id} className="listrow" data-testid={`${p.one}-ligne`} data-name={t.name} style={{ flexWrap: 'wrap' }}>
              <div className="listrow-main" style={{ minWidth: 0, flex: '1 1 180px' }}>
                <span className="listrow-title" style={{ overflowWrap: 'anywhere' }}>{t.name}</span>
                <span className="listrow-meta" data-testid={`${p.one}-compte`}>{plural(n(t.id))}</span>
              </div>
              {p.writer && <span className="listrow-end" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button size="sm" onClick={() => open({ kind: 'rename', item: t })}>Renommer</Button>
                <Button size="sm" disabled={items.length < 2} onClick={() => open({ kind: 'merge', item: t })}>Fusionner</Button>
                <Button size="sm" variant="quiet" onClick={() => open({ kind: 'delete', item: t })}>Supprimer</Button>
              </span>}
            </div>
          ))}
        </div>}
      <p className="f-help" style={{ margin: '12px 0 0' }} data-testid={`${p.kind}-plafond`}>{full ? p.capLine : p.capCount(items.length)}</p>

      <Modal open={d?.kind === 'new'} title={p.newLabel} onClose={close}
        actions={<><Button onClick={close}>Annuler</Button><Button variant="primary" disabled={busy} onClick={create}>Créer</Button></>}>
        <Field label="Nom" error={error ?? undefined}>{id => <Input id={id} aria-label={p.nameLabel} value={name} autoFocus placeholder={p.placeholder}
          onChange={e => { setName(e.target.value); setError(null) }} onKeyDown={e => { if (e.key === 'Enter') create() }} />}</Field>
      </Modal>
      {d?.kind === 'rename' && <Modal open title={`Renommer « ${d.item.name} »`} onClose={close}
        actions={<><Button onClick={close}>Annuler</Button><Button variant="primary" disabled={busy} onClick={() => rename(d.item)}>Renommer</Button></>}>
        <Field label="Nouveau nom" error={error ?? undefined}>{id => <Input id={id} aria-label="Nouveau nom" value={name} autoFocus
          onChange={e => { setName(e.target.value); setError(null) }} onKeyDown={e => { if (e.key === 'Enter') rename(d.item) }} />}</Field>
        <p className="f-help" style={{ margin: '8px 0 0' }} data-testid={`${p.kind}-renommage-effet`}>{`${plural(n(d.item.id))} ${n(d.item.id) > 1 ? 'gardent' : 'garde'} ${p.thisOne} sous son nouveau nom.`}</p>
      </Modal>}
      {d?.kind === 'merge' && <Modal open title={`Fusionner « ${d.item.name} »`} onClose={close}
        actions={<><Button onClick={close}>Annuler</Button><Button variant="primary" disabled={!target || busy} onClick={() => merge(d.item)}>Fusionner</Button></>}>
        <Field label={p.intoLabel}>{id => <Select id={id} aria-label={p.intoLabel} value={into} onChange={e => setInto(e.target.value)}>
          {items.filter(t => t.id !== d.item.id).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </Select>}</Field>
        <p className="t-body" style={{ margin: '8px 0 0' }} data-testid={`${p.kind}-fusion-effet`}>{target ? `${plural(n(d.item.id))} ${n(d.item.id) > 1 ? 'passent' : 'passe'} sous « ${target.name} », qui reste. « ${d.item.name} » disparaît.` : ''}</p>
        {refused}
      </Modal>}
      {d?.kind === 'delete' && <Modal open title={`Supprimer « ${d.item.name} » ?`} onClose={close}
        actions={<><Button onClick={close}>Annuler</Button><Button variant="danger" data-testid={`${p.kind}-supprimer-ok`} disabled={busy} onClick={() => remove(d.item)}>Supprimer</Button></>}>
        <p className="t-body" style={{ margin: 0 }} data-testid={`${p.kind}-suppression-effet`}>{p.deleteEffect(plural(n(d.item.id)))}</p>
        {refused}
      </Modal>}
    </Card>
  )
}
